import React, { useEffect, useState } from "react";
import { Search } from "lucide-react";
import { api } from "@/lib/apiClient";
import JobCard from "@/components/site/JobCard";

const TYPES = ["All", "Contract", "Full-time", "Part-time"];

export default function JobsPage() {
  const [jobs, setJobs] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("All");
  const [jobType, setJobType] = useState("All");
  const [remote, setRemote] = useState(false);
  const [categories, setCategories] = useState(["All"]);
  const [loading, setLoading] = useState(true);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (category !== "All") params.category = category;
      if (jobType !== "All") params.job_type = jobType;
      if (remote) params.remote = true;
      const r = await api.get("/jobs", { params });
      setJobs(r.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/jobs/categories").then((r) => setCategories(["All", ...(r.data.categories || [])])).catch(() => {});
  }, []);

  useEffect(() => {
    const id = setTimeout(fetchJobs, 250);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, category, jobType, remote]);

  return (
    <div data-testid="jobs-page" className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-16 sm:py-24">
      <div className="text-xs uppercase tracking-[0.28em] font-semibold text-[#D35400]">Open positions</div>
      <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-semibold text-[#1A2E24] max-w-3xl">
        Real contracts. Real relocations.
      </h1>
      <p className="mt-6 text-[#5C6B64] max-w-2xl leading-relaxed">
        Every listing here is a signed contract with a US farm, homestead, or operator.
        Visa sponsorship is stated on each card — no fine print.
      </p>

      {/* Filter bar */}
      <div className="mt-12 border border-[#DCD7C9] bg-white p-4 sm:p-6 grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
        <div className="md:col-span-5 relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6B64]" />
          <input
            data-testid="jobs-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search jobs, keywords, cities…"
            className="w-full border border-[#DCD7C9] pl-10 pr-4 py-3 text-sm bg-white focus:outline-none focus:border-[#1A2E24]"
          />
        </div>
        <select
          data-testid="jobs-filter-category"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="md:col-span-3 border border-[#DCD7C9] px-4 py-3 text-sm bg-white focus:outline-none focus:border-[#1A2E24]"
        >
          {categories.map((c) => (<option key={c} value={c}>{c === "All" ? "All categories" : c}</option>))}
        </select>
        <select
          data-testid="jobs-filter-type"
          value={jobType}
          onChange={(e) => setJobType(e.target.value)}
          className="md:col-span-2 border border-[#DCD7C9] px-4 py-3 text-sm bg-white focus:outline-none focus:border-[#1A2E24]"
        >
          {TYPES.map((c) => (<option key={c} value={c}>{c === "All" ? "All types" : c}</option>))}
        </select>
        <label className="md:col-span-2 flex items-center gap-3 text-sm text-[#1A2E24] cursor-pointer select-none">
          <input
            data-testid="jobs-filter-remote"
            type="checkbox"
            checked={remote}
            onChange={(e) => setRemote(e.target.checked)}
            className="h-4 w-4 accent-[#D35400]"
          />
          Remote only
        </label>
      </div>

      {/* Result count */}
      <div className="mt-8 text-xs uppercase tracking-widest text-[#5C6B64]" data-testid="jobs-count">
        {loading ? "Loading…" : `${jobs.length} contract${jobs.length === 1 ? "" : "s"} available`}
      </div>

      {/* Job list */}
      <div className="mt-6 grid grid-cols-1 gap-4">
        {jobs.map((j, i) => (<JobCard key={j.id} job={j} index={i} />))}
        {!loading && jobs.length === 0 && (
          <div className="text-sm text-[#5C6B64] py-12 text-center border border-dashed border-[#DCD7C9]">
            No jobs match your filters. Try adjusting the search.
          </div>
        )}
      </div>
    </div>
  );
}
